import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, CheckCircle2, AlertCircle, Trash2, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle } from "./DashboardLayout";
import { Button } from "@/components/ui/button";

export default function Content() {
  const { user } = useAuth();
  const { data: content, refetch } = useQuery({ queryKey: ["all-content"], queryFn: () => api.get("/content").then(r => r.data.content) });
  const [hero, setHero] = useState({ headline: "", subheadline: "" });
  const [contact, setContact] = useState({ phone: "", email: "", area: "", hours: "" });
  const [about, setAbout] = useState("");
  const [greeting, setGreeting] = useState("");
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    if (content) {
      setHero(content.home_hero || {});
      setContact(content.contact_info || {});
      setAbout(content.about?.body || "");
      setGreeting(content.ai_greeting || "");
      setFaqs(Array.isArray(content.faqs) ? content.faqs : []);
    }
  }, [content]);

  const save = async (key, value) => {
    try { await api.put(`/content/${key}`, { value }); toast.success("Saved — live on the site now"); refetch(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";

  return (
    <div>
      <PageTitle title="Website Content" subtitle="Edit public content — changes appear instantly, no redeploy" />
      <div className="space-y-6 max-w-2xl">
        <Section title="Homepage Hero" onSave={() => save("home_hero", hero)}>
          <input className={input} value={hero.headline || ""} onChange={e => setHero({ ...hero, headline: e.target.value })} placeholder="Headline" data-testid="content-hero-headline" />
          <textarea className={`${input} h-auto py-2`} rows={3} value={hero.subheadline || ""} onChange={e => setHero({ ...hero, subheadline: e.target.value })} placeholder="Subheadline" data-testid="content-hero-sub" />
        </Section>
        <Section title="Contact Info" onSave={() => save("contact_info", contact)}>
          <div className="grid grid-cols-2 gap-3">
            <input className={input} value={contact.phone || ""} onChange={e => setContact({ ...contact, phone: e.target.value })} placeholder="Phone" data-testid="content-phone" />
            <input className={input} value={contact.email || ""} onChange={e => setContact({ ...contact, email: e.target.value })} placeholder="Email" />
            <input className={input} value={contact.area || ""} onChange={e => setContact({ ...contact, area: e.target.value })} placeholder="Service area" />
            <input className={input} value={contact.hours || ""} onChange={e => setContact({ ...contact, hours: e.target.value })} placeholder="Hours" />
          </div>
        </Section>
        <Section title="About Page" onSave={() => save("about", { body: about })}>
          <textarea className={`${input} h-auto py-2`} rows={5} value={about} onChange={e => setAbout(e.target.value)} data-testid="content-about" />
        </Section>
        <Section title="Nexus AI Greeting" onSave={() => save("ai_greeting", greeting)}>
          <textarea className={`${input} h-auto py-2`} rows={3} value={greeting} onChange={e => setGreeting(e.target.value)} data-testid="content-greeting" />
        </Section>
        <Section title="FAQs" onSave={() => save("faqs", faqs.filter(f => f.q?.trim()))}>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`faq-item-${i}`}>
                <div className="flex gap-2">
                  <input className={input} placeholder="Question" value={f.q || ""} onChange={e => setFaqs(faqs.map((x, idx) => idx === i ? { ...x, q: e.target.value } : x))} data-testid={`faq-q-${i}`} />
                  <button type="button" onClick={() => setFaqs(faqs.filter((_, idx) => idx !== i))} className="p-2 text-red-500 hover:bg-red-50 rounded shrink-0" data-testid={`faq-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                </div>
                <textarea className={`${input} h-auto py-2`} rows={2} placeholder="Answer" value={f.a || ""} onChange={e => setFaqs(faqs.map((x, idx) => idx === i ? { ...x, a: e.target.value } : x))} data-testid={`faq-a-${i}`} />
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setFaqs([...faqs, { q: "", a: "" }])} className="gap-1.5" data-testid="add-faq-btn"><Plus className="h-4 w-4" /> Add FAQ</Button>
          </div>
        </Section>
        {(user?.role === "owner" || user?.role === "admin") && <EmailPanel />}
      </div>
    </div>
  );
}

function EmailPanel() {
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get("/email/status").then(r => setStatus(r.data)).catch(() => {}); }, []);
  const test = async () => {
    if (!to) return;
    setSending(true);
    try { const r = await api.post("/email/test", { to }); toast.success(r.data.message); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5" data-testid="email-panel">
      <h3 className="font-heading font-semibold text-navy mb-3 flex items-center gap-2"><Mail className="h-4 w-4 text-electric" /> Email Delivery</h3>
      {status && (
        <div className={`text-sm flex items-center gap-1.5 mb-3 ${status.enabled ? "text-green-600" : "text-amber-600"}`}>
          {status.enabled ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {status.enabled ? `Connected · sending from ${status.sender}` : "Not configured"}
        </div>
      )}
      <p className="text-xs text-slate-500 mb-3">{status?.note}</p>
      <div className="flex gap-2">
        <input className={input} type="email" placeholder="Send a test to…" value={to} onChange={e => setTo(e.target.value)} data-testid="email-test-to" />
        <Button onClick={test} disabled={sending} className="bg-electric hover:bg-tech shrink-0" data-testid="email-test-send">Send Test</Button>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">Automated emails fire on: contact messages, service requests, appointments, estimates sent, invoice payments, and digital sales.</p>
    </div>
  );
}

function Section({ title, children, onSave }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5">
      <h3 className="font-heading font-semibold text-navy mb-3">{title}</h3>
      <div className="space-y-3">{children}</div>
      <Button onClick={onSave} className="mt-4 bg-electric hover:bg-tech" size="sm" data-testid={`save-${title.toLowerCase().replace(/ /g, "-")}`}>Save</Button>
    </div>
  );
}
